'use client';

import React, { useState, useEffect, useRef, useCallback, useReducer } from 'react';
import Image from 'next/image';

// ─── Types ───────────────────────────────────────────────────────────────────

type Language = 'en' | 'hi' | 'gu' | 'te' | 'ta' | 'kn';

interface InsightCard {
  label: string;
  value: string;
}

interface VinaResponse {
  text: string;
  cards: InsightCard[];
}

interface QuestionSet {
  questions: string[];
  responses: Record<string, VinaResponse>;
  placeholder: string;
  promptLabel: string;
  askVinaLabel: string;
  instructionLabel: string;
  thinkingLabel: string;
  askAnotherLabel: string;
  inputPlaceholder: string;
  selectLanguageLabel: string;
  startHereLabel: string;
  suggestedLabel: string;
}

// ─── Auto-demo sequence ───────────────────────────────────────────────────────

const DEMO_LANG_SEQUENCE: Language[] = ['en', 'hi', 'gu', 'te', 'ta', 'kn'];
const TOTAL_STEPS = 6;

// Demo state machine phases
type DemoPhase = 'idle' | 'question-appear' | 'thinking' | 'responding' | 'cards' | 'pause';

interface DemoState {
  stepIndex: number;       // 0–5
  phase: DemoPhase;
  isRunning: boolean;
  userPaused: boolean;     // hover/touch pause
  manualLangOverride: boolean;
  manualLangResumeAt: number; // timestamp when manual override expires
}

type DemoAction =
  | { type: 'START' }
  | { type: 'SET_PHASE'; phase: DemoPhase }
  | { type: 'NEXT_STEP' }
  | { type: 'USER_PAUSE' }
  | { type: 'USER_RESUME' }
  | { type: 'MANUAL_LANG'; resumeAt: number }
  | { type: 'CLEAR_MANUAL_LANG' }
  | { type: 'USER_INTERACTED' }
  | { type: 'RESUME_AUTO' };

function demoReducer(state: DemoState, action: DemoAction): DemoState {
  switch (action.type) {
    case 'START':
      return { ...state, isRunning: true, stepIndex: 0, phase: 'question-appear' };
    case 'SET_PHASE':
      return { ...state, phase: action.phase };
    case 'NEXT_STEP':
      return { ...state, stepIndex: (state.stepIndex + 1) % TOTAL_STEPS, phase: 'question-appear' };
    case 'USER_PAUSE':
      return { ...state, userPaused: true };
    case 'USER_RESUME':
      return { ...state, userPaused: false };
    case 'MANUAL_LANG':
      return { ...state, manualLangOverride: true, manualLangResumeAt: action.resumeAt };
    case 'CLEAR_MANUAL_LANG':
      return { ...state, manualLangOverride: false, manualLangResumeAt: 0 };
    case 'USER_INTERACTED':
      return { ...state, isRunning: false, userPaused: false };
    case 'RESUME_AUTO':
      return { ...state, isRunning: true, phase: 'question-appear' };
    default:
      return state;
  }
}

// ─── Language Content ─────────────────────────────────────────────────────────

const LANG_NAMES: Record<Language, string> = {
  en: 'English',
  hi: 'हिंदी',
  gu: 'ગુજરાતી',
  te: 'తెలుగు',
  ta: 'தமிழ்',
  kn: 'ಕನ್ನಡ',
};

const CONTENT: Record<Language, QuestionSet> = {
  en: {
    placeholder: 'Try asking something like…',
    promptLabel: 'Ask VINA anything about your business.',
    askVinaLabel: 'Ask VINA',
    instructionLabel: 'Choose a question to see how VINA responds.',
    thinkingLabel: 'VINA is thinking…',
    askAnotherLabel: 'Ask another question →',
    inputPlaceholder: 'Ask VINA anything about your business…',
    selectLanguageLabel: 'Select Language',
    startHereLabel: 'START HERE',
    suggestedLabel: 'Suggested',
    questions: [
      'Hey VINA, tell me about yourself. What can you do for my business?',
      'Which products should I reorder this week?',
      'Which outlet is growing fastest?',
      'What needs my attention today?',
      'What changed in my business this week?',
      'Where am I losing margin?',
    ],
    responses: {
      'Hey VINA, tell me about yourself. What can you do for my business?': {
        text: "I help you understand your business, spot what matters, and act faster.\n\nAsk me about sales, inventory, procurement, payments, outlets, or daily performance.",
        cards: [
          { label: 'Prediction & Forecasting', value: 'Active' },
          { label: 'Daily Updates', value: 'Ready' },
          { label: 'Procurement', value: 'Enabled' },
        ],
      },
      'Which products should I reorder this week?': {
        text: '7 products may need replenishment.\n\nSnacks and Dairy are moving fastest — recommend placing orders before the weekend.',
        cards: [
          { label: '7 Products', value: 'Potential replenishment' },
          { label: 'Demand Trend', value: '+21%' },
          { label: 'Revenue Potential', value: '₹3.2L' },
        ],
      },
      'Which outlet is growing fastest?': {
        text: 'Outlet #3 is growing fastest.\n\n+18% revenue growth this month, driven by Snacks and Beverages.',
        cards: [
          { label: 'Revenue Growth', value: '+18%' },
          { label: 'Customer Growth', value: '+14%' },
          { label: 'Inventory Turnover', value: '+16%' },
        ],
      },
      'What needs my attention today?': {
        text: "Here are 5 things worth your attention today.\n\n3 products are low on stock, 2 purchase orders are pending approval, and 1 outlet is underperforming.",
        cards: [
          { label: 'Inventory', value: '3 Products' },
          { label: 'Collections', value: '2 Orders' },
          { label: 'Sales', value: '1 Outlet' },
        ],
      },
      'What changed in my business this week?': {
        text: 'Sales are up 12%, while 3 products may need replenishment.\n\nCustomer repeat rate improved and average basket size increased by ₹180.',
        cards: [
          { label: 'Revenue Change', value: '+12%' },
          { label: 'Avg Basket', value: '+₹180' },
          { label: 'Repeat Rate', value: '+8%' },
        ],
      },
      'Where am I losing margin?': {
        text: 'Margin is under pressure in 3 product categories.\n\nTwo outlets are selling below recommended price on 4 SKUs.',
        cards: [
          { label: 'Margin Gap', value: '4 SKUs' },
          { label: 'Affected Outlets', value: '2 Outlets' },
          { label: 'Recovery Potential', value: '₹1.8L' },
        ],
      },
    },
  },
  hi: {
    placeholder: 'कुछ पूछकर देखें…',
    promptLabel: 'VINA से अपने business के बारे में कुछ भी पूछें।',
    askVinaLabel: 'VINA से पूछें',
    instructionLabel: 'एक सवाल चुनें और देखें VINA कैसे जवाब देती है।',
    thinkingLabel: 'VINA सोच रही है…',
    askAnotherLabel: 'कोई और सवाल पूछें →',
    inputPlaceholder: 'VINA से अपने business के बारे में कुछ भी पूछें…',
    selectLanguageLabel: 'भाषा चुनें',
    startHereLabel: 'यहाँ से शुरू करें',
    suggestedLabel: 'सुझाए गए सवाल',
    questions: [
      'Hey VINA, अपने बारे में बताओ। मेरे business के लिए क्या कर सकती हो?',
      'इस हफ्ते कौनसे products दोबारा order करने चाहिए?',
      'कौनसी outlet सबसे तेज़ grow कर रही है?',
      'आज मुझे किन चीज़ों पर ध्यान देना चाहिए?',
      'इस हफ्ते business में क्या बदला?',
      'कहाँ margin कम हो रहा है?',
    ],
    responses: {
      'Hey VINA, अपने बारे में बताओ। मेरे business के लिए क्या कर सकती हो?': {
        text: 'मैं आपके business को समझने, ज़रूरी चीज़ें पहचानने और तेज़ फैसले लेने में मदद करती हूँ।\n\nSales, inventory, procurement, payments — कुछ भी पूछें।',
        cards: [
          { label: 'Prediction & Forecasting', value: 'Active' },
          { label: 'Daily Updates', value: 'Ready' },
          { label: 'Procurement', value: 'Enabled' },
        ],
      },
      'इस हफ्ते कौनसे products दोबारा order करने चाहिए?': {
        text: '7 products को इस हफ्ते reorder करने की जरूरत पड़ सकती है।\n\nSnacks और Dairy सबसे तेज़ बिक रहे हैं — weekend से पहले order करना सही रहेगा।',
        cards: [
          { label: '7 Products', value: 'Reorder जरूरी' },
          { label: 'Demand Trend', value: '+21%' },
          { label: 'Revenue Potential', value: '₹3.2L' },
        ],
      },
      'कौनसी outlet सबसे तेज़ grow कर रही है?': {
        text: 'Outlet #3 सबसे तेज़ grow कर रही है।\n\n+18% revenue growth इस महीने। Snacks और Beverages की वजह से।',
        cards: [
          { label: 'Revenue Growth', value: '+18%' },
          { label: 'Customer Growth', value: '+14%' },
          { label: 'Inventory Turnover', value: '+16%' },
        ],
      },
      'आज मुझे किन चीज़ों पर ध्यान देना चाहिए?': {
        text: 'आज 5 चीज़ें ध्यान देने वाली हैं।\n\n3 products का stock कम है, 2 purchase orders approve होने बाकी हैं, 1 outlet कम perform कर रही है।',
        cards: [
          { label: 'Inventory', value: '3 Products' },
          { label: 'Collections', value: '2 Orders' },
          { label: 'Sales', value: '1 Outlet' },
        ],
      },
      'इस हफ्ते business में क्या बदला?': {
        text: 'Sales 12% बढ़ी हैं, जबकि 3 products को reorder की ज़रूरत हो सकती है।\n\nCustomers की repeat rate बेहतर हुई और average basket ₹180 बढ़ा।',
        cards: [
          { label: 'Revenue Change', value: '+12%' },
          { label: 'Avg Basket', value: '+₹180' },
          { label: 'Repeat Rate', value: '+8%' },
        ],
      },
      'कहाँ margin कम हो रहा है?': {
        text: '3 product categories में margin पर दबाव है।\n\nदो outlets में 4 SKUs recommended price से कम पर बिक रहे हैं।',
        cards: [
          { label: 'Margin Gap', value: '4 SKUs' },
          { label: 'Affected Outlets', value: '2 Outlets' },
          { label: 'Recovery Potential', value: '₹1.8L' },
        ],
      },
    },
  },
  gu: {
    placeholder: 'કંઈ પૂછીને જુઓ…',
    promptLabel: 'VINA ને તમારા business વિશે કંઈ પણ પૂછો.',
    askVinaLabel: 'VINA ને પૂછો',
    instructionLabel: 'એક સવાલ પસંદ કરો અને જુઓ VINA કેવી રીતે જવાબ આપે છે.',
    thinkingLabel: 'VINA વિચારી રહી છે…',
    askAnotherLabel: 'બીજો સવાલ પૂછો →',
    inputPlaceholder: 'VINA ને તમારા business વિશે કંઈ પણ પૂછો…',
    selectLanguageLabel: 'ભાષા પસંદ કરો',
    startHereLabel: 'અહીંથી શરૂ કરો',
    suggestedLabel: 'સૂચવેલ સવાલો',
    questions: [
      'Hey VINA, તારા વિશે જણાવ. મારા business માટે શું કરી શકે?',
      'આ અઠવાડિયે કયા products ફરીથી order કરવા જોઈએ?',
      'કઈ outlet સૌથી ઝડપથી grow કરી રહી છે?',
      'આજે મારે શેના પર ધ્યાન આપવું જોઈએ?',
      'આ અઠવાડિયે business માં શું બદલાયું?',
      'ક્યાં margin ઘટી રહ્યો છે?',
    ],
    responses: {
      'Hey VINA, તારા વિશે જણાવ. મારા business માટે શું કરી શકે?': {
        text: 'હું તમારા business ને સમજવામાં, ઝડપથી નિર્ણય લેવામાં અને ધ્યાન આપવા જેવી બાબતો ઓળખવામાં મદદ કરું છું.\n\nSales, inventory, procurement — ગમે તે પૂછો.',
        cards: [
          { label: 'Prediction & Forecasting', value: 'Active' },
          { label: 'Daily Updates', value: 'Ready' },
          { label: 'Procurement', value: 'Enabled' },
        ],
      },
      'આ અઠવાડિયે કયા products ફરીથી order કરવા જોઈએ?': {
        text: '7 products ને આ અઠવાડિયે reorder કરવાની જરૂર પડી શકે.\n\nSnacks અને Dairy સૌથી ઝડપથી વેચાઈ રહ્યા છે — weekend પહેલાં order કરવું સારું.',
        cards: [
          { label: '7 Products', value: 'Reorder જરૂરી' },
          { label: 'Demand Trend', value: '+21%' },
          { label: 'Revenue Potential', value: '₹3.2L' },
        ],
      },
      'કઈ outlet સૌથી ઝડપથી grow કરી રહી છે?': {
        text: 'Outlet #3 આ મહિને સૌથી ઝડપથી grow કરી રહી છે.\n\n+18% revenue growth. Snacks અને Beverages ની વજ઼ہ ثے.',
        cards: [
          { label: 'Revenue Growth', value: '+18%' },
          { label: 'Customer Growth', value: '+14%' },
          { label: 'Inventory Turnover', value: '+16%' },
        ],
      },
      'આજે મારે શેના પર ધ્યાન આપવું જોઈએ?': {
        text: 'આજે 5 બાબતો ધ્યાન આપવા જેવી છે.\n\n3 products નો stock ઓછો છે, 2 purchase orders approve થવાના બાકી છે, 1 outlet ઓછું perform કરી રહી છે.',
        cards: [
          { label: 'Inventory', value: '3 Products' },
          { label: 'Collections', value: '2 Orders' },
          { label: 'Sales', value: '1 Outlet' },
        ],
      },
      'આ અઠવાડિયે business માં શું બદલાયું?': {
        text: 'Sales 12% વધ્યો છે, જ્યારે 3 products ને reorder ની જરૂર હોઈ શકે.\n\nCustomers ની repeat rate સુધરી અને average basket ₹180 વધ્યો.',
        cards: [
          { label: 'Revenue Change', value: '+12%' },
          { label: 'Avg Basket', value: '+₹180' },
          { label: 'Repeat Rate', value: '+8%' },
        ],
      },
      'ક્યાં margin ઘટી રહ્યો છે?': {
        text: '3 product categories માં margin પર દબાણ છે.\n\nબે outlets માં 4 SKUs recommended price કરતાં ઓછા ભાવે વેચાઈ રહ્યા છે.',
        cards: [
          { label: 'Margin Gap', value: '4 SKUs' },
          { label: 'Affected Outlets', value: '2 Outlets' },
          { label: 'Recovery Potential', value: '₹1.8L' },
        ],
      },
    },
  },
  te: {
    placeholder: 'ఏదైనా అడిగి చూడండి…',
    promptLabel: 'మీ business గురించి VINA ని ఏదైనా అడగండి.',
    askVinaLabel: 'VINA ని అడగండి',
    instructionLabel: 'ఒక ప్రశ్న ఎంచుకోండి, VINA ఎలా సమాధానం ఇస్తుందో చూడండి.',
    thinkingLabel: 'VINA ఆలోచిస్తోంది…',
    askAnotherLabel: 'మరో ప్రశ్న అడగండి →',
    inputPlaceholder: 'మీ business గురించి VINA ని ఏదైనా అడగండి…',
    selectLanguageLabel: 'భాష ఎంచుకోండి',
    startHereLabel: 'ఇక్కడ నుండి ప్రారంభించండి',
    suggestedLabel: 'సూచించిన ప్రశ్నలు',
    questions: [
      'Hey VINA, నీ గురించి చెప్పు. నా business కి ఏం చేయగలవు?',
      'ఈ వారం ఏ products మళ్ళీ order చేయాలి?',
      'ఏ outlet అత్యంత వేగంగా grow అవుతోంది?',
      'ఈ రోజు నేను దేనిపై దృష్టి పెట్టాలి?',
      'ఈ వారం business లో ఏం మారింది?',
      'ఎక్కడ margin తగ్గుతోంది?',
    ],
    responses: {
      'Hey VINA, నీ గురించి చెప్పు. నా business కి ఏం చేయగలవు?': {
        text: 'మీ business ని అర్థం చేసుకోవడంలో, ముఖ్యమైన విషయాలు గుర్తించడంలో, వేగంగా నిర్ణయాలు తీసుకోవడంలో నేను సహాయం చేస్తాను.\n\nSales, inventory, procurement — ఏదైనా అడగండి.',
        cards: [
          { label: 'Prediction & Forecasting', value: 'Active' },
          { label: 'Daily Updates', value: 'Ready' },
          { label: 'Procurement', value: 'Enabled' },
        ],
      },
      'ఈ వారం ఏ products మళ్ళీ order చేయాలి?': {
        text: '7 products ఈ వారం reorder అవసరం కావచ్చు.\n\nSnacks మరియు Dairy అత్యంత వేగంగా అమ్ముడవుతున్నాయి — weekend కంటే ముందే order చేయడం మంచిది.',
        cards: [
          { label: '7 Products', value: 'Reorder అవసరం' },
          { label: 'Demand Trend', value: '+21%' },
          { label: 'Revenue Potential', value: '₹3.2L' },
        ],
      },
      'ఏ outlet అత్యంత వేగంగా grow అవుతోంది?': {
        text: 'Outlet #3 అత్యంత వేగంగా grow అవుతోంది.\n\n+18% revenue growth ఈ నెల. Snacks మరియు Beverages వల్ల.',
        cards: [
          { label: 'Revenue Growth', value: '+18%' },
          { label: 'Customer Growth', value: '+14%' },
          { label: 'Inventory Turnover', value: '+16%' },
        ],
      },
      'ఈ రోజు నేను దేనిపై దృష్టి పెట్టాలి?': {
        text: 'ఈ రోజు 5 విషయాలు దృష్టి పెట్టాలి.\n\n3 products stock తక్కువగా ఉంది, 2 purchase orders approve కావాల్సి ఉంది, 1 outlet తక్కువగా perform చేస్తోంది.',
        cards: [
          { label: 'Inventory', value: '3 Products' },
          { label: 'Collections', value: '2 Orders' },
          { label: 'Sales', value: '1 Outlet' },
        ],
      },
      'ఈ వారం business లో ఏం మారింది?': {
        text: 'Sales 12% పెరిగాయి, 3 products reorder అవసరం కావచ్చు.\n\nCustomers repeat rate మెరుగుపడింది మరియు average basket ₹180 పెరిగింది.',
        cards: [
          { label: 'Revenue Change', value: '+12%' },
          { label: 'Avg Basket', value: '+₹180' },
          { label: 'Repeat Rate', value: '+8%' },
        ],
      },
      'ఎక్కడ margin తగ్గుతోంది?': {
        text: '3 product categories లో margin పై ఒత్తిడి ఉంది.\n\nరెండు outlets లో 4 SKUs recommended price కంటే తక్కువకు అమ్ముడవుతున్నాయి.',
        cards: [
          { label: 'Margin Gap', value: '4 SKUs' },
          { label: 'Affected Outlets', value: '2 Outlets' },
          { label: 'Recovery Potential', value: '₹1.8L' },
        ],
      },
    },
  },
  ta: {
    placeholder: 'ஏதாவது கேட்டுப் பாருங்கள்…',
    promptLabel: 'உங்கள் business பற்றி VINA-ஐ எதுவும் கேளுங்கள்.',
    askVinaLabel: 'VINA-ஐ கேளுங்கள்',
    instructionLabel: 'ஒரு கேள்வியை தேர்ந்தெடுங்கள், VINA எப்படி பதில் சொல்கிறது என்று பாருங்கள்.',
    thinkingLabel: 'VINA யோசிக்கிறது…',
    askAnotherLabel: 'இன்னொரு கேள்வி கேளுங்கள் →',
    inputPlaceholder: 'உங்கள் business பற்றி VINA-ஐ எதுவும் கேளுங்கள்…',
    selectLanguageLabel: 'மொழி தேர்ந்தெடுங்கள்',
    startHereLabel: 'இங்கே தொடங்குங்கள்',
    suggestedLabel: 'பரிந்துரைக்கப்பட்ட கேள்விகள்',
    questions: [
      'Hey VINA, உன்னைப் பற்றி சொல்லு. என் business-க்கு என்ன செய்யலாம்?',
      'இந்த வாரம் எந்த products மீண்டும் order செய்ய வேண்டும்?',
      'எந்த outlet மிக வேகமாக grow ஆகிறது?',
      'இன்று நான் எதில் கவனம் செலுத்த வேண்டும்?',
      'இந்த வாரம் business-ல் என்ன மாறியது?',
      'எங்கே margin குறைகிறது?',
    ],
    responses: {
      'Hey VINA, உன்னைப் பற்றி சொல்லு. என் business-க்கு என்ன செய்யலாம்?': {
        text: 'உங்கள் business-ஐ புரிந்துகொள்ள, முக்கியமான விஷயங்களை கண்டறிய, வேகமாக முடிவெடுக்க நான் உதவுகிறேன்.\n\nSales, inventory, procurement — எதுவும் கேளுங்கள்.',
        cards: [
          { label: 'Prediction & Forecasting', value: 'Active' },
          { label: 'Daily Updates', value: 'Ready' },
          { label: 'Procurement', value: 'Enabled' },
        ],
      },
      'இந்த வாரம் எந்த products மீண்டும் order செய்ய வேண்டும்?': {
        text: '7 products இந்த வாரம் reorder தேவைப்படலாம்.\n\nSnacks மற்றும் Dairy மிக வேகமாக விற்கின்றன — weekend-க்கு முன்பே order செய்வது நல்லது.',
        cards: [
          { label: '7 Products', value: 'Reorder தேவை' },
          { label: 'Demand Trend', value: '+21%' },
          { label: 'Revenue Potential', value: '₹3.2L' },
        ],
      },
      'எந்த outlet மிக வேகமாக grow ஆகிறது?': {
        text: 'Outlet #3 மிக வேகமாக grow ஆகிறது.\n\n+18% revenue growth இந்த மாதம். Snacks மற்றும் Beverages காரணமாக.',
        cards: [
          { label: 'Revenue Growth', value: '+18%' },
          { label: 'Customer Growth', value: '+14%' },
          { label: 'Inventory Turnover', value: '+16%' },
        ],
      },
      'இன்று நான் எதில் கவனம் செலுத்த வேண்டும்?': {
        text: 'இன்று 5 விஷயங்களில் கவனம் செலுத்த வேண்டும்.\n\n3 products stock குறைவாக உள்ளது, 2 purchase orders approve ஆக வேண்டியுள்ளது, 1 outlet குறைவாக செயல்படுகிறது.',
        cards: [
          { label: 'Inventory', value: '3 Products' },
          { label: 'Collections', value: '2 Orders' },
          { label: 'Sales', value: '1 Outlet' },
        ],
      },
      'இந்த வாரம் business-ல் என்ன மாறியது?': {
        text: 'Sales 12% அதிகரித்துள்ளது, 3 products reorder தேவைப்படலாம்.\n\nCustomers repeat rate மேம்பட்டது, average basket ₹180 அதிகரித்தது.',
        cards: [
          { label: 'Revenue Change', value: '+12%' },
          { label: 'Avg Basket', value: '+₹180' },
          { label: 'Repeat Rate', value: '+8%' },
        ],
      },
      'எங்கே margin குறைகிறது?': {
        text: '3 product categories-ல் margin-ல் அழுத்தம் உள்ளது.\n\nஇரண்டு outlets-ல் 4 SKUs recommended price-ஐ விட குறைவாக விற்கப்படுகின்றன.',
        cards: [
          { label: 'Margin Gap', value: '4 SKUs' },
          { label: 'Affected Outlets', value: '2 Outlets' },
          { label: 'Recovery Potential', value: '₹1.8L' },
        ],
      },
    },
  },
  kn: {
    placeholder: 'ಏನಾದರೂ ಕೇಳಿ ನೋಡಿ…',
    promptLabel: 'ನಿಮ್ಮ business ಬಗ್ಗೆ VINA ಗೆ ಏನಾದರೂ ಕೇಳಿ.',
    askVinaLabel: 'VINA ಗೆ ಕೇಳಿ',
    instructionLabel: 'ಒಂದು ಪ್ರಶ್ನೆ ಆಯ್ಕೆ ಮಾಡಿ, VINA ಹೇಗೆ ಉತ್ತರಿಸುತ್ತದೆ ಎಂದು ನೋಡಿ.',
    thinkingLabel: 'VINA ಯೋಚಿಸುತ್ತಿದೆ…',
    askAnotherLabel: 'ಇನ್ನೊಂದು ಪ್ರಶ್ನೆ ಕೇಳಿ →',
    inputPlaceholder: 'ನಿಮ್ಮ business ಬಗ್ಗೆ VINA ಗೆ ಏನಾದರೂ ಕೇಳಿ…',
    selectLanguageLabel: 'ಭಾಷೆ ಆಯ್ಕೆ ಮಾಡಿ',
    startHereLabel: 'ಇಲ್ಲಿಂದ ಪ್ರಾರಂಭಿಸಿ',
    suggestedLabel: 'ಸೂಚಿಸಿದ ಪ್ರಶ್ನೆಗಳು',
    questions: [
      'Hey VINA, ನಿನ್ನ ಬಗ್ಗೆ ಹೇಳು. ನನ್ನ business ಗೆ ಏನು ಮಾಡಬಲ್ಲೆ?',
      'ಈ ವಾರ ಯಾವ products ಮತ್ತೆ order ಮಾಡಬೇಕು?',
      'ಯಾವ outlet ಅತ್ಯಂತ ವೇಗವಾಗಿ grow ಆಗುತ್ತಿದೆ?',
      'ಇಂದು ನಾನು ಯಾವುದರ ಮೇಲೆ ಗಮನ ಹರಿಸಬೇಕು?',
      'ಈ ವಾರ business ನಲ್ಲಿ ಏನು ಬದಲಾಯಿತು?',
      'ಎಲ್ಲಿ margin ಕಡಿಮೆಯಾಗುತ್ತಿದೆ?',
    ],
    responses: {
      'Hey VINA, ನಿನ್ನ ಬಗ್ಗೆ ಹೇಳು. ನನ್ನ business ಗೆ ಏನು ಮಾಡಬಲ್ಲೆ?': {
        text: 'ನಿಮ್ಮ business ಅರ್ಥಮಾಡಿಕೊಳ್ಳಲು, ಮುಖ್ಯ ವಿಷಯಗಳನ್ನು ಗುರುತಿಸಲು, ವೇಗವಾಗಿ ನಿರ್ಧಾರ ತೆಗೆದುಕೊಳ್ಳಲು ನಾನು ಸಹಾಯ ಮಾಡುತ್ತೇನೆ.\n\nSales, inventory, procurement — ಏನಾದರೂ ಕೇಳಿ.',
        cards: [
          { label: 'Prediction & Forecasting', value: 'Active' },
          { label: 'Daily Updates', value: 'Ready' },
          { label: 'Procurement', value: 'Enabled' },
        ],
      },
      'ಈ ವಾರ ಯಾವ products ಮತ್ತೆ order ಮಾಡಬೇಕು?': {
        text: '7 products ಈ ವಾರ reorder ಅಗತ್ಯವಾಗಬಹುದು.\n\nSnacks ಮತ್ತು Dairy ಅತ್ಯಂತ ವೇಗವಾಗಿ ಮಾರಾಟವಾಗುತ್ತಿವೆ — weekend ಮೊದಲೇ order ಮಾಡುವುದು ಒಳ್ಳೆಯದು.',
        cards: [
          { label: '7 Products', value: 'Reorder ಅಗತ್ಯ' },
          { label: 'Demand Trend', value: '+21%' },
          { label: 'Revenue Potential', value: '₹3.2L' },
        ],
      },
      'ಯಾವ outlet ಅತ್ಯಂತ ವೇಗವಾಗಿ grow ಆಗುತ್ತಿದೆ?': {
        text: 'Outlet #3 ಅತ್ಯಂತ ವೇಗವಾಗಿ grow ಆಗುತ್ತಿದೆ.\n\n+18% revenue growth ಈ ತಿಂಗಳು. Snacks ಮತ್ತು Beverages ಕಾರಣದಿಂದ.',
        cards: [
          { label: 'Revenue Growth', value: '+18%' },
          { label: 'Customer Growth', value: '+14%' },
          { label: 'Inventory Turnover', value: '+16%' },
        ],
      },
      'ಇಂದು ನಾನು ಯಾವುದರ ಮೇಲೆ ಗಮನ ಹರಿಸಬೇಕು?': {
        text: 'ಇಂದು 5 ವಿಷಯಗಳ ಮೇಲೆ ಗಮನ ಹರಿಸಬೇಕು.\n\n3 products stock ಕಡಿಮೆ ಇದೆ, 2 purchase orders approve ಆಗಬೇಕಿದೆ, 1 outlet ಕಡಿಮೆ perform ಮಾಡುತ್ತಿದೆ.',
        cards: [
          { label: 'Inventory', value: '3 Products' },
          { label: 'Collections', value: '2 Orders' },
          { label: 'Sales', value: '1 Outlet' },
        ],
      },
      'ಈ ವಾರ business ನಲ್ಲಿ ಏನು ಬದಲಾಯಿತು?': {
        text: 'Sales 12% ಹೆಚ್ಚಾಗಿದೆ, 3 products reorder ಅಗತ್ಯವಾಗಬಹುದು.\n\nCustomers repeat rate ಸುಧಾರಿಸಿದೆ ಮತ್ತು average basket ₹180 ಹೆಚ್ಚಾಗಿದೆ.',
        cards: [
          { label: 'Revenue Change', value: '+12%' },
          { label: 'Avg Basket', value: '+₹180' },
          { label: 'Repeat Rate', value: '+8%' },
        ],
      },
      'ಎಲ್ಲಿ margin ಕಡಿಮೆಯಾಗುತ್ತಿದೆ?': {
        text: '3 product categories ನಲ್ಲಿ margin ಒತ್ತಡದಲ್ಲಿದೆ.\n\nಎರಡು outlets ನಲ್ಲಿ 4 SKUs recommended price ಗಿಂತ ಕಡಿಮೆ ಬೆಲೆಗೆ ಮಾರಾಟವಾಗುತ್ತಿವೆ.',
        cards: [
          { label: 'Margin Gap', value: '4 SKUs' },
          { label: 'Affected Outlets', value: '2 Outlets' },
          { label: 'Recovery Potential', value: '₹1.8L' },
        ],
      },
    },
  },
};

// ─── Timing constants (ms) ────────────────────────────────────────────────────
const T_QUESTION_APPEAR = 1200;   // question bubble appears, then thinking starts
const T_THINKING = 1000;          // thinking dots duration
const T_RESPONSE_REVEAL = 1800;   // fast reveal (not char-by-char in auto mode)
const T_CARDS_VISIBLE = 800;      // cards fade in after response
const T_PAUSE_BEFORE_NEXT = 2800; // read time before next question
const T_IDLE_RESUME = 8000;       // resume auto after user idle
const T_MANUAL_LANG_RESUME = 12000; // resume auto lang after manual selection

// ─── Sub-components ───────────────────────────────────────────────────────────

function VinaThinkingDots({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2" aria-label={label}>
      <div className="flex items-center gap-1">
        {[0, 1, 2].map(i => (
          <span
            key={i}
            style={{
              width: '5px',
              height: '5px',
              borderRadius: '50%',
              background: '#1F4D3F',
              display: 'inline-block',
              animation: `vinaThink 1.2s ease-in-out ${i * 0.2}s infinite`,
              opacity: 0.6,
            }}
          />
        ))}
      </div>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: '#7B8F88', letterSpacing: '0.04em' }}>
        {label}
      </span>
    </div>
  );
}

function InsightChip({ label, value }: InsightCard) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '7px 12px',
        borderRadius: '10px',
        background: 'rgba(31,77,63,0.06)',
        border: '1px solid rgba(31,77,63,0.12)',
      }}
    >
      <span style={{ fontSize: '11px', color: '#5D6B66', fontFamily: 'var(--font-sans)', fontWeight: 450 }}>{label}</span>
      <span
        style={{
          fontSize: '12px',
          fontWeight: 600,
          color: '#1F4D3F',
          fontFamily: 'var(--font-mono)',
          letterSpacing: '-0.01em',
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function HeroSection() {
  const [mounted, setMounted] = useState(false);
  const [lang, setLang] = useState<Language>('en');
  const [langOpen, setLangOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [pulseActive, setPulseActive] = useState(false);
  const [customInput, setCustomInput] = useState(false ? '' : '');
  const [customInputValue, setCustomInputValue] = useState('');
  const [hoveredQ, setHoveredQ] = useState<number | null>(null);

  // Manual interaction state (user clicked a question or typed)
  const [manualQ, setManualQ] = useState<string | null>(null);
  const [manualPhase, setManualPhase] = useState<'idle' | 'thinking' | 'responding' | 'cards'>('idle');
  const [manualResponseVisible, setManualResponseVisible] = useState(false);
  const [manualCardsVisible, setManualCardsVisible] = useState(false);
  const [typedManualResponse, setTypedManualResponse] = useState('');
  const [manualTypingDone, setManualTypingDone] = useState(false);

  // Auto-demo state
  const [demoState, dispatch] = useReducer(demoReducer, {
    stepIndex: 0,
    phase: 'idle',
    isRunning: false,
    userPaused: false,
    manualLangOverride: false,
    manualLangResumeAt: 0,
  });

  // Auto-demo display state (what's shown in the panel)
  const [demoQuestion, setDemoQuestion] = useState<string>('');
  const [demoResponse, setDemoResponse] = useState<string>('');
  const [demoCards, setDemoCards] = useState<InsightCard[]>([]);
  const [demoPhaseDisplay, setDemoPhaseDisplay] = useState<'hidden' | 'question' | 'thinking' | 'responding' | 'cards'>('hidden');

  const langRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const conversationRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);

  // Timer refs for cleanup
  const demoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleResumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const manualTypingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const manualCardsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const manualLangTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isUserMode = manualPhase !== 'idle';
  const prefersReducedMotion = useRef(false);

  // Detect reduced motion
  useEffect(() => {
    if (typeof window !== 'undefined') {
      prefersReducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }, []);

  useEffect(() => {
    setMounted(true);
    const t = setTimeout(() => setPulseActive(true), 600);
    return () => clearTimeout(t);
  }, []);

  // Clear a timer helper
  const clearTimer = useCallback((ref: React.MutableRefObject<ReturnType<typeof setTimeout> | null>) => {
    if (ref.current) {
      clearTimeout(ref.current);
      ref.current = null;
    }
  }, []);

  // ── Viewport detection — start demo when hero enters viewport ──
  useEffect(() => {
    if (!mounted) return;
    if (prefersReducedMotion.current) return;

    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !demoState.isRunning) {
          // Small delay before starting
          demoTimerRef.current = setTimeout(() => {
            dispatch({ type: 'START' });
          }, 600);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      clearTimer(demoTimerRef);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]);

  // ── Auto-demo sequence runner ──
  useEffect(() => {
    if (!demoState.isRunning || demoState.userPaused || isUserMode) return;
    if (prefersReducedMotion.current) return;

    const stepIdx = demoState.stepIndex;
    const demoLang = demoState.manualLangOverride ? lang : DEMO_LANG_SEQUENCE[stepIdx];
    const langContent = CONTENT[demoLang];
    const question = langContent.questions[stepIdx];
    const response = langContent.responses[question];

    if (demoState.phase === 'question-appear') {
      // Update lang if not overridden
      if (!demoState.manualLangOverride) {
        setLang(demoLang);
      }
      setDemoQuestion(question);
      setDemoResponse(response?.text ?? '');
      setDemoCards(response?.cards ?? []);
      setDemoPhaseDisplay('question');

      demoTimerRef.current = setTimeout(() => {
        dispatch({ type: 'SET_PHASE', phase: 'thinking' });
      }, T_QUESTION_APPEAR);
    }

    if (demoState.phase === 'thinking') {
      setDemoPhaseDisplay('thinking');
      demoTimerRef.current = setTimeout(() => {
        dispatch({ type: 'SET_PHASE', phase: 'responding' });
      }, T_THINKING);
    }

    if (demoState.phase === 'responding') {
      setDemoPhaseDisplay('responding');
      demoTimerRef.current = setTimeout(() => {
        dispatch({ type: 'SET_PHASE', phase: 'cards' });
      }, T_RESPONSE_REVEAL);
    }

    if (demoState.phase === 'cards') {
      setDemoPhaseDisplay('cards');
      demoTimerRef.current = setTimeout(() => {
        dispatch({ type: 'SET_PHASE', phase: 'pause' });
      }, T_CARDS_VISIBLE);
    }

    if (demoState.phase === 'pause') {
      demoTimerRef.current = setTimeout(() => {
        dispatch({ type: 'NEXT_STEP' });
      }, T_PAUSE_BEFORE_NEXT);
    }

    return () => clearTimer(demoTimerRef);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoState.phase, demoState.stepIndex, demoState.isRunning, demoState.userPaused, isUserMode, lang]);

  // Auto-scroll conversation area
  useEffect(() => {
    if (conversationRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
  }, [demoPhaseDisplay, manualCardsVisible, manualResponseVisible]);

  // Close lang dropdown on outside click
  useEffect(() => {
    if (!langOpen) return;
    function handler(e: MouseEvent) {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [langOpen]);

  // ── Hover/touch pause ──
  const handlePanelMouseEnter = useCallback(() => {
    if (demoState.isRunning) dispatch({ type: 'USER_PAUSE' });
  }, [demoState.isRunning]);

  const handlePanelMouseLeave = useCallback(() => {
    if (demoState.userPaused && !isUserMode) {
      dispatch({ type: 'USER_RESUME' });
    }
  }, [demoState.userPaused, isUserMode]);

  // ── Manual question click ──
  const triggerManualQuestion = useCallback((q: string, langKey: Language) => {
    // Stop auto demo
    clearTimer(demoTimerRef);
    clearTimer(manualTypingTimerRef);
    clearTimer(manualCardsTimerRef);
    dispatch({ type: 'USER_INTERACTED' });
    setDemoPhaseDisplay('hidden');

    const responseText = CONTENT[langKey].responses[q]?.text ?? '';
    const responseCards = CONTENT[langKey].responses[q]?.cards ?? [];

    setManualQ(q);
    setManualPhase('thinking');
    setManualResponseVisible(false);
    setManualCardsVisible(false);
    setTypedManualResponse('');
    setManualTypingDone(false);

    // Thinking → responding
    manualTypingTimerRef.current = setTimeout(() => {
      setManualPhase('responding');
      setManualResponseVisible(true);

      // Char-by-char typing
      let i = 0;
      const speed = 12;
      function tick() {
        i++;
        setTypedManualResponse(responseText.slice(0, i));
        if (i < responseText.length) {
          manualTypingTimerRef.current = setTimeout(tick, speed);
        } else {
          setManualTypingDone(true);
          // Show cards after typing
          manualCardsTimerRef.current = setTimeout(() => {
            setManualCardsVisible(true);
            setManualPhase('cards');
          }, 600);
        }
      }
      manualTypingTimerRef.current = setTimeout(tick, speed);
    }, 1200);

    // Schedule auto-resume after idle
    clearTimer(idleResumeTimerRef);
    idleResumeTimerRef.current = setTimeout(() => {
      setManualQ(null);
      setManualPhase('idle');
      setManualResponseVisible(false);
      setManualCardsVisible(false);
      setDemoPhaseDisplay('hidden');
      dispatch({ type: 'RESUME_AUTO' });
    }, T_IDLE_RESUME + responseText.length * 12 + 3000);
  }, [clearTimer]);

  const handleQuestionClick = useCallback((q: string) => {
    triggerManualQuestion(q, lang);
  }, [lang, triggerManualQuestion]);

  const handleAskAnother = useCallback(() => {
    clearTimer(manualTypingTimerRef);
    clearTimer(manualCardsTimerRef);
    clearTimer(idleResumeTimerRef);
    setManualQ(null);
    setManualPhase('idle');
    setManualResponseVisible(false);
    setManualCardsVisible(false);
    setTypedManualResponse('');
    setManualTypingDone(false);
    setDemoPhaseDisplay('hidden');
    // Resume auto after a short pause
    idleResumeTimerRef.current = setTimeout(() => {
      dispatch({ type: 'RESUME_AUTO' });
    }, 2000);
  }, [clearTimer]);

  const handleCustomSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!customInputValue.trim() || manualPhase === 'thinking') return;
    const matchedQ = CONTENT[lang].questions.find(q =>
      q.toLowerCase().includes(customInputValue.toLowerCase().slice(0, 8))
    ) ?? CONTENT[lang].questions[0];
    setCustomInputValue('');
    triggerManualQuestion(matchedQ, lang);
  }, [customInputValue, manualPhase, lang, triggerManualQuestion]);

  const handleLangChange = useCallback((l: Language) => {
    setLang(l);
    setLangOpen(false);

    // Mark manual lang override
    const resumeAt = Date.now() + T_MANUAL_LANG_RESUME;
    dispatch({ type: 'MANUAL_LANG', resumeAt });
    clearTimer(manualLangTimerRef);
    manualLangTimerRef.current = setTimeout(() => {
      dispatch({ type: 'CLEAR_MANUAL_LANG' });
    }, T_MANUAL_LANG_RESUME);

    // If user is in manual mode, re-trigger current question in new lang
    if (isUserMode && manualQ) {
      const oldIdx = CONTENT[lang].questions.indexOf(manualQ);
      if (oldIdx !== -1) {
        const newQ = CONTENT[l].questions[oldIdx];
        triggerManualQuestion(newQ, l);
      }
    }
  }, [lang, isUserMode, manualQ, triggerManualQuestion, clearTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimer(demoTimerRef);
      clearTimer(idleResumeTimerRef);
      clearTimer(manualTypingTimerRef);
      clearTimer(manualCardsTimerRef);
      clearTimer(manualLangTimerRef);
    };
  }, [clearTimer]);

  const content = CONTENT[lang];
  const currentQuestions = content.questions;

  // Determine what to show in the panel
  const showDemoConversation = !isUserMode && demoPhaseDisplay !== 'hidden';
  const showManualConversation = isUserMode && manualQ !== null;
  const showSuggestedQuestions = !isUserMode && demoPhaseDisplay === 'hidden';

  // Current demo response data
  const demoLangForDisplay = demoState.manualLangOverride ? lang : DEMO_LANG_SEQUENCE[demoState.stepIndex];
  const demoContent = CONTENT[demoLangForDisplay];

  if (!mounted) return null;

  return (
    <section
      id="hero"
      ref={sectionRef}
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
      style={{
        background: 'linear-gradient(160deg, #E6F1FB 0%, #EEF4F8 30%, #F6F5F0 65%, #FBFAF6 100%)',
        paddingTop: '80px',
      }}
      aria-label="Hero section"
    >
      {/* Ambient orbs */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute top-[8%] left-[12%] w-[520px] h-[520px] rounded-full opacity-30"
          style={{ background: 'radial-gradient(circle, rgba(31,77,63,0.09) 0%, transparent 70%)', filter: 'blur(60px)' }}
        />
        <div
          className="absolute bottom-[15%] right-[8%] w-[400px] h-[400px] rounded-full opacity-25"
          style={{ background: 'radial-gradient(circle, rgba(118,147,160,0.12) 0%, transparent 70%)', filter: 'blur(50px)' }}
        />
        <div
          className="absolute top-[40%] right-[30%] w-[300px] h-[300px] rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(200,220,240,0.18) 0%, transparent 70%)', filter: 'blur(40px)' }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center min-h-[calc(100vh-80px)] py-12 lg:py-16">

          {/* ── LEFT — Editorial copy ── */}
          <div className="flex flex-col gap-6 lg:gap-7">
            {/* Eyebrow */}
            <div className="flex items-center gap-3">
              <div
                className="flex items-center gap-2 px-3 py-1.5 rounded-full"
                style={{ background: 'rgba(31,77,63,0.07)', border: '1px solid rgba(31,77,63,0.12)' }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{
                    background: '#1F4D3F',
                    animation: pulseActive ? 'pulse 2s cubic-bezier(0.4,0,0.6,1) infinite' : 'none',
                  }}
                />
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '11px',
                    letterSpacing: '0.06em',
                    color: '#1F4D3F',
                    textTransform: 'uppercase',
                  }}
                >
                  VINA · Business Intelligence
                </span>
              </div>
            </div>

            {/* Headline */}
            <h1
              style={{
                fontFamily: 'var(--font-sans)',
                fontWeight: 600,
                fontSize: 'clamp(2.2rem, 4.8vw, 3.8rem)',
                lineHeight: 1.08,
                letterSpacing: '-0.03em',
                color: '#0D2620',
              }}
            >
              How much of your
              <br />
              business runs on{' '}
              <span
                style={{
                  color: '#1F4D3F',
                  fontStyle: 'italic',
                  position: 'relative',
                  display: 'inline-block',
                }}
              >
                memory?
              </span>
            </h1>

            {/* Supporting copy */}
            <p
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'clamp(0.95rem, 1.6vw, 1.1rem)',
                lineHeight: 1.72,
                color: '#3D5550',
                maxWidth: '460px',
                fontWeight: 400,
              }}
            >
              vina turns your business data into answers, insights, and actions - so you spend less time remembering and more time deciding.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3 items-center">
              <a
                href="https://vasy.ai/india-avail-free-demo?utm_source=lp&utm_medium=web&utm_campaign=teamvina"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-medium text-sm transition-all"
                style={{
                  background: '#1F4D3F',
                  color: '#FFFFFF',
                  letterSpacing: '-0.01em',
                  boxShadow: '0 4px 20px rgba(31,77,63,0.25)',
                  fontSize: '14px',
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.background = '#29584B';
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 6px 28px rgba(31,77,63,0.35)';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.background = '#1F4D3F';
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 20px rgba(31,77,63,0.25)';
                }}
              >
                Schedule a Free Demo Now
              </a>
              <button
                id="video"
                type="button"
                onClick={() => setVideoOpen(true)}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-medium text-sm transition-all"
                style={{
                  color: '#1F4D3F',
                  border: '1px solid rgba(31,77,63,0.2)',
                  background: 'rgba(31,77,63,0.04)',
                  fontSize: '14px',
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.background = 'rgba(31,77,63,0.08)';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.background = 'rgba(31,77,63,0.04)';
                }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <circle cx="7" cy="7" r="6" stroke="#1F4D3F" strokeWidth="1.2" />
                  <path d="M5.5 4.5L9.5 7L5.5 9.5V4.5Z" fill="#1F4D3F" />
                </svg>
                Watch the story
              </button>
            </div>

            {/* Trust micro-copy */}
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: '#7B8F88', letterSpacing: '0.01em' }}>
              Powered by Vasy · Built for growing businesses
            </p>
          </div>

          {/* ── RIGHT — Interactive VINA Panel ── */}
          <div className="relative flex items-center justify-center lg:justify-end">
            {/* Ambient glow */}
            <div
              className="absolute -z-10 w-[380px] h-[380px] rounded-full pointer-events-none"
              style={{
                background: 'radial-gradient(circle, rgba(31,77,63,0.07) 0%, transparent 70%)',
                filter: 'blur(40px)',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
              aria-hidden="true"
            />

            <div
              ref={panelRef}
              className="relative w-full"
              style={{
                maxWidth: '520px',
                background: 'rgba(255,255,255,0.88)',
                backdropFilter: 'blur(24px)',
                WebkitBackdropFilter: 'blur(24px)',
                border: '1px solid rgba(211,223,219,0.75)',
                borderRadius: '24px',
                boxShadow: '0 24px 64px rgba(31,77,63,0.09), 0 4px 16px rgba(118,147,160,0.08)',
                overflow: 'hidden',
              }}
              role="region"
              aria-label="VINA interactive panel"
              onMouseEnter={handlePanelMouseEnter}
              onMouseLeave={handlePanelMouseLeave}
              onTouchStart={handlePanelMouseEnter}
              onTouchEnd={handlePanelMouseLeave}
            >
              {/* ── Panel Header ── */}
              <div
                className="flex items-center justify-between px-5 py-3.5"
                style={{ borderBottom: '1px solid rgba(211,223,219,0.6)', background: 'rgba(255,255,255,0.6)' }}
              >
                <div className="flex items-center gap-2.5">
                  <Image
                    src="/assets/images/vina-lockup-inline-512-1786271505989.png"
                    alt="VINA"
                    width={52}
                    height={18}
                    style={{ objectFit: 'contain', height: 'auto' }}
                    priority
                  />
                  <span
                    className="px-2 py-0.5 rounded-full"
                    style={{
                      background: 'rgba(79,155,122,0.12)',
                      color: '#2D6A5A',
                      fontFamily: 'var(--font-mono)',
                      fontSize: '9px',
                      letterSpacing: '0.06em',
                      fontWeight: 600,
                    }}
                  >
                    LIVE
                  </span>
                </div>

                {/* Language selector */}
                <div className="relative" ref={langRef}>
                  <button
                    onClick={() => setLangOpen(v => !v)}
                    className="flex items-center gap-1.5 rounded-lg transition-all"
                    style={{
                      padding: '6px 10px',
                      background: langOpen ? 'rgba(31,77,63,0.08)' : 'rgba(31,77,63,0.04)',
                      border: '1px solid rgba(31,77,63,0.16)',
                      cursor: 'pointer',
                    }}
                    aria-label="Select language"
                    aria-expanded={langOpen}
                    aria-haspopup="listbox"
                  >
                    <span style={{
                      fontFamily: 'var(--font-sans)',
                      fontSize: '11.5px',
                      color: '#1F4D3F',
                      fontWeight: 500,
                      letterSpacing: '-0.01em',
                      whiteSpace: 'nowrap',
                    }}>
                      {lang === 'en' ? content.selectLanguageLabel : LANG_NAMES[lang]}
                    </span>
                    <svg
                      width="9"
                      height="9"
                      viewBox="0 0 10 10"
                      fill="none"
                      aria-hidden="true"
                      style={{
                        transform: langOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                        flexShrink: 0,
                      }}
                    >
                      <path d="M2 3.5L5 6.5L8 3.5" stroke="#1F4D3F" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>

                  {langOpen && (
                    <div
                      className="absolute right-0 top-full mt-1.5 py-1 rounded-xl overflow-hidden z-50"
                      style={{
                        background: 'rgba(255,255,255,0.98)',
                        backdropFilter: 'blur(16px)',
                        WebkitBackdropFilter: 'blur(16px)',
                        border: '1px solid rgba(211,223,219,0.8)',
                        boxShadow: '0 12px 32px rgba(31,77,63,0.14)',
                        minWidth: '152px',
                        animation: 'dropIn 0.15s cubic-bezier(0.22,1,0.36,1)',
                      }}
                      role="listbox"
                      aria-label="Language options"
                    >
                      {(Object.keys(LANG_NAMES) as Language[]).map(l => (
                        <button
                          key={l}
                          onClick={() => handleLangChange(l)}
                          className="w-full flex items-center justify-between px-3.5 py-2.5 transition-all text-left"
                          style={{
                            background: lang === l ? 'rgba(31,77,63,0.07)' : 'transparent',
                            cursor: 'pointer',
                          }}
                          role="option"
                          aria-selected={lang === l}
                          onMouseEnter={e => { if (lang !== l) (e.currentTarget as HTMLElement).style.background = 'rgba(31,77,63,0.04)'; }}
                          onMouseLeave={e => { if (lang !== l) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                        >
                          <span style={{ fontFamily: 'var(--font-sans)', fontSize: '13px', color: '#1A3530', fontWeight: lang === l ? 500 : 400 }}>
                            {LANG_NAMES[l]}
                          </span>
                          {lang === l && (
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                              <path d="M2 6L5 9L10 3" stroke="#1F4D3F" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Ask VINA heading + instruction / demo status ── */}
              <div
                className="px-5 pt-4 pb-3"
                style={{ borderBottom: '1px solid rgba(211,223,219,0.4)' }}
              >
                <div className="flex items-center justify-between gap-2">
                  <p style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: '15px',
                    color: '#0D2620',
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    marginBottom: '3px',
                  }}>
                    {content.askVinaLabel}
                  </p>
                  {/* Live demo indicator */}
                  {showDemoConversation && !isUserMode && (
                    <div
                      className="flex items-center gap-1.5 flex-shrink-0"
                      style={{
                        padding: '3px 8px',
                        borderRadius: '20px',
                        background: 'rgba(31,77,63,0.06)',
                        border: '1px solid rgba(31,77,63,0.1)',
                        animation: 'fadeSlideUp 0.3s ease',
                      }}
                    >
                      <span
                        style={{
                          width: '5px',
                          height: '5px',
                          borderRadius: '50%',
                          background: '#2D6A5A',
                          display: 'inline-block',
                          animation: 'pulse 2s cubic-bezier(0.4,0,0.6,1) infinite',
                          flexShrink: 0,
                        }}
                      />
                      <span style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: '9px',
                        color: '#2D6A5A',
                        letterSpacing: '0.04em',
                        whiteSpace: 'nowrap',
                      }}>
                        LIVE DEMO
                      </span>
                    </div>
                  )}
                </div>
                <p style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: '12.5px',
                  color: '#7B8F88',
                  fontWeight: 400,
                  lineHeight: 1.5,
                }}>
                  {showDemoConversation && !isUserMode
                    ? 'VINA is showing you what it can do'
                    : content.instructionLabel}
                </p>
              </div>

              {/* ── AUTO DEMO conversation area ── */}
              {showDemoConversation && (
                <div
                  ref={conversationRef}
                  className="px-5 py-4 flex flex-col gap-3"
                  style={{
                    maxHeight: '240px',
                    overflowY: 'auto',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                  }}
                >
                  {/* User question bubble */}
                  {(demoPhaseDisplay === 'question' || demoPhaseDisplay === 'thinking' || demoPhaseDisplay === 'responding' || demoPhaseDisplay === 'cards') && (
                    <div className="flex justify-end" style={{ animation: 'fadeSlideUp 0.4s cubic-bezier(0.22,1,0.36,1)' }}>
                      <div
                        style={{
                          maxWidth: '85%',
                          padding: '10px 14px',
                          borderRadius: '16px 16px 4px 16px',
                          background: '#1F4D3F',
                          color: '#FFFFFF',
                          fontFamily: 'var(--font-sans)',
                          fontSize: '13px',
                          lineHeight: 1.5,
                          fontWeight: 400,
                        }}
                      >
                        {demoQuestion}
                      </div>
                    </div>
                  )}

                  {/* VINA thinking / response */}
                  {(demoPhaseDisplay === 'thinking' || demoPhaseDisplay === 'responding' || demoPhaseDisplay === 'cards') && (
                    <div className="flex items-start gap-2.5">
                      <div
                        className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
                        style={{ background: 'rgba(31,77,63,0.08)', border: '1px solid rgba(31,77,63,0.12)' }}
                        aria-hidden="true"
                      >
                        <Image
                          src="/assets/images/vina-mark-256-1786271470852.png"
                          alt=""
                          width={14}
                          height={14}
                          style={{ objectFit: 'contain' }}
                        />
                      </div>

                      <div className="flex-1">
                        {demoPhaseDisplay === 'thinking' && (
                          <div
                            style={{
                              padding: '10px 14px',
                              borderRadius: '4px 16px 16px 16px',
                              background: 'rgba(31,77,63,0.05)',
                              border: '1px solid rgba(31,77,63,0.08)',
                              display: 'inline-flex',
                              animation: 'fadeSlideUp 0.25s ease',
                            }}
                          >
                            <VinaThinkingDots label={demoContent.thinkingLabel} />
                          </div>
                        )}

                        {(demoPhaseDisplay === 'responding' || demoPhaseDisplay === 'cards') && (
                          <div
                            style={{
                              padding: '12px 14px',
                              borderRadius: '4px 16px 16px 16px',
                              background: 'rgba(255,255,255,0.9)',
                              border: '1px solid rgba(211,223,219,0.7)',
                              fontFamily: 'var(--font-sans)',
                              fontSize: '13px',
                              color: '#1A3530',
                              lineHeight: 1.65,
                              fontWeight: 400,
                              whiteSpace: 'pre-line',
                              animation: 'fadeSlideUp 0.35s cubic-bezier(0.22,1,0.36,1)',
                            }}
                          >
                            {demoResponse}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Insight chips */}
                  {demoPhaseDisplay === 'cards' && demoCards.length > 0 && (
                    <div
                      className="flex flex-wrap gap-2 pl-9"
                      style={{ animation: 'fadeSlideUp 0.4s cubic-bezier(0.22,1,0.36,1)' }}
                    >
                      {demoCards.map((card, i) => (
                        <InsightChip key={i} label={card.label} value={card.value} />
                      ))}
                    </div>
                  )}

                  {/* Step indicator dots */}
                  {(demoPhaseDisplay === 'responding' || demoPhaseDisplay === 'cards') && (
                    <div className="flex items-center gap-1.5 pl-9" style={{ animation: 'fadeSlideUp 0.5s ease' }}>
                      {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                        <span
                          key={i}
                          style={{
                            width: i === demoState.stepIndex ? '14px' : '5px',
                            height: '5px',
                            borderRadius: '3px',
                            background: i === demoState.stepIndex ? '#1F4D3F' : 'rgba(31,77,63,0.2)',
                            transition: 'all 0.3s ease',
                            display: 'inline-block',
                          }}
                        />
                      ))}
                      <span style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: '9px',
                        color: '#9AADA7',
                        letterSpacing: '0.04em',
                        marginLeft: '4px',
                      }}>
                        {demoState.stepIndex + 1}/{TOTAL_STEPS}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* ── MANUAL conversation area ── */}
              {showManualConversation && (
                <div
                  ref={conversationRef}
                  className="px-5 py-4 flex flex-col gap-3"
                  style={{
                    maxHeight: '240px',
                    overflowY: 'auto',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                  }}
                >
                  {/* User question bubble */}
                  <div className="flex justify-end">
                    <div
                      style={{
                        maxWidth: '85%',
                        padding: '10px 14px',
                        borderRadius: '16px 16px 4px 16px',
                        background: '#1F4D3F',
                        color: '#FFFFFF',
                        fontFamily: 'var(--font-sans)',
                        fontSize: '13px',
                        lineHeight: 1.5,
                        fontWeight: 400,
                        animation: 'fadeSlideUp 0.3s cubic-bezier(0.22,1,0.36,1)',
                      }}
                    >
                      {manualQ}
                    </div>
                  </div>

                  {/* VINA thinking / response */}
                  <div className="flex items-start gap-2.5">
                    <div
                      className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
                      style={{ background: 'rgba(31,77,63,0.08)', border: '1px solid rgba(31,77,63,0.12)' }}
                      aria-hidden="true"
                    >
                      <Image
                        src="/assets/images/vina-mark-256-1786271470852.png"
                        alt=""
                        width={14}
                        height={14}
                        style={{ objectFit: 'contain' }}
                      />
                    </div>

                    <div className="flex-1">
                      {manualPhase === 'thinking' && (
                        <div
                          style={{
                            padding: '10px 14px',
                            borderRadius: '4px 16px 16px 16px',
                            background: 'rgba(31,77,63,0.05)',
                            border: '1px solid rgba(31,77,63,0.08)',
                            display: 'inline-flex',
                            animation: 'fadeSlideUp 0.25s ease',
                          }}
                        >
                          <VinaThinkingDots label={content.thinkingLabel} />
                        </div>
                      )}

                      {(manualPhase === 'responding' || manualPhase === 'cards') && manualResponseVisible && (
                        <div
                          style={{
                            padding: '12px 14px',
                            borderRadius: '4px 16px 16px 16px',
                            background: 'rgba(255,255,255,0.9)',
                            border: '1px solid rgba(211,223,219,0.7)',
                            fontFamily: 'var(--font-sans)',
                            fontSize: '13px',
                            color: '#1A3530',
                            lineHeight: 1.65,
                            fontWeight: 400,
                            whiteSpace: 'pre-line',
                            animation: 'fadeSlideUp 0.3s cubic-bezier(0.22,1,0.36,1)',
                          }}
                        >
                          {typedManualResponse}
                          {!manualTypingDone && (
                            <span
                              style={{
                                display: 'inline-block',
                                width: '2px',
                                height: '13px',
                                background: '#1F4D3F',
                                marginLeft: '2px',
                                verticalAlign: 'middle',
                                animation: 'cursorBlink 0.7s step-end infinite',
                              }}
                              aria-hidden="true"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Insight chips */}
                  {manualCardsVisible && manualQ && (
                    <div
                      className="flex flex-wrap gap-2 pl-9"
                      style={{ animation: 'fadeSlideUp 0.4s cubic-bezier(0.22,1,0.36,1)' }}
                    >
                      {(content.responses[manualQ]?.cards ?? []).map((card, i) => (
                        <InsightChip key={i} label={card.label} value={card.value} />
                      ))}
                    </div>
                  )}

                  {/* Ask another */}
                  {manualCardsVisible && (
                    <div
                      className="pl-9"
                      style={{ animation: 'fadeSlideUp 0.5s cubic-bezier(0.22,1,0.36,1)' }}
                    >
                      <button
                        onClick={handleAskAnother}
                        style={{
                          fontFamily: 'var(--font-sans)',
                          fontSize: '12.5px',
                          color: '#1F4D3F',
                          fontWeight: 500,
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px 0',
                          letterSpacing: '-0.01em',
                          textDecoration: 'underline',
                          textDecorationColor: 'rgba(31,77,63,0.3)',
                          textUnderlineOffset: '3px',
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.textDecorationColor = 'rgba(31,77,63,0.8)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.textDecorationColor = 'rgba(31,77,63,0.3)'; }}
                      >
                        {content.askAnotherLabel}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* ── Suggested questions (shown only when demo not running and no manual interaction) ── */}
              {showSuggestedQuestions && (
                <div className="px-5 pt-4 pb-4">
                  <p style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '10px',
                    color: '#9AADA7',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    marginBottom: '10px',
                  }}>
                    {content.suggestedLabel}
                  </p>
                  <div className="flex flex-col gap-2">
                    {currentQuestions.map((q, i) => {
                      const isFirst = i === 0;
                      const isHovered = hoveredQ === i;
                      return (
                        <div key={`${lang}-${i}`}>
                          {isFirst && (
                            <div className="mb-1.5">
                              <span
                                style={{
                                  fontFamily: 'var(--font-mono)',
                                  fontSize: '9px',
                                  letterSpacing: '0.08em',
                                  color: '#2D6A5A',
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  background: 'rgba(31,77,63,0.08)',
                                  padding: '2px 7px',
                                  borderRadius: '4px',
                                  display: 'inline-block',
                                }}
                              >
                                {content.startHereLabel}
                              </span>
                            </div>
                          )}
                          <button
                            onClick={() => handleQuestionClick(q)}
                            onMouseEnter={() => setHoveredQ(i)}
                            onMouseLeave={() => setHoveredQ(null)}
                            onFocus={() => setHoveredQ(i)}
                            onBlur={() => setHoveredQ(null)}
                            className="text-left w-full"
                            style={{
                              padding: isFirst ? '11px 14px' : '9px 13px',
                              borderRadius: '12px',
                              background: isFirst
                                ? (isHovered ? 'rgba(31,77,63,0.1)' : 'rgba(31,77,63,0.06)')
                                : (isHovered ? 'rgba(31,77,63,0.07)' : 'rgba(31,77,63,0.03)'),
                              border: isFirst
                                ? `1.5px solid ${isHovered ? 'rgba(31,77,63,0.28)' : 'rgba(31,77,63,0.18)'}`
                                : `1px solid ${isHovered ? 'rgba(31,77,63,0.18)' : 'rgba(31,77,63,0.09)'}`,
                              fontFamily: 'var(--font-sans)',
                              fontSize: isFirst ? '13px' : '12.5px',
                              color: isFirst ? '#1A3530' : (isHovered ? '#1A3530' : '#3D5550'),
                              fontWeight: isFirst ? 500 : 400,
                              lineHeight: 1.45,
                              cursor: 'pointer',
                              transition: 'all 0.16s ease',
                              transform: isHovered ? 'translateX(2px)' : 'translateX(0)',
                              boxShadow: isFirst
                                ? (isHovered ? '0 4px 14px rgba(31,77,63,0.12)' : '0 2px 8px rgba(31,77,63,0.07)')
                                : (isHovered ? '0 2px 8px rgba(31,77,63,0.08)' : 'none'),
                            }}
                            aria-label={`Ask VINA: ${q}`}
                          >
                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                              <span style={{ flex: 1 }}>{q}</span>
                              <span
                                style={{
                                  flexShrink: 0,
                                  fontSize: '13px',
                                  color: isHovered ? '#1F4D3F' : 'rgba(31,77,63,0.25)',
                                  transition: 'all 0.16s ease',
                                  transform: isHovered ? 'translateX(2px)' : 'translateX(0)',
                                  fontWeight: 400,
                                  lineHeight: 1,
                                }}
                                aria-hidden="true"
                              >
                                →
                              </span>
                            </span>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Custom input ── */}
              <div
                className="px-5 pb-5"
                style={{ borderTop: '1px solid rgba(211,223,219,0.4)', paddingTop: '14px' }}
              >
                <form onSubmit={handleCustomSubmit} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={customInputValue}
                    onChange={e => setCustomInputValue(e.target.value)}
                    placeholder={content.inputPlaceholder}
                    aria-label={content.inputPlaceholder}
                    style={{
                      flex: 1,
                      padding: '9px 13px',
                      borderRadius: '10px',
                      border: '1px solid rgba(211,223,219,0.8)',
                      background: 'rgba(246,245,240,0.7)',
                      fontFamily: 'var(--font-sans)',
                      fontSize: '12.5px',
                      color: '#1A3530',
                      outline: 'none',
                      transition: 'border-color 0.15s ease',
                      minWidth: 0,
                    }}
                    onFocus={e => {
                      (e.currentTarget as HTMLInputElement).style.borderColor = 'rgba(31,77,63,0.35)';
                      // Pause demo when user focuses input
                      if (demoState.isRunning) dispatch({ type: 'USER_PAUSE' });
                    }}
                    onBlur={e => {
                      (e.currentTarget as HTMLInputElement).style.borderColor = 'rgba(211,223,219,0.8)';
                    }}
                  />
                  <button
                    type="submit"
                    disabled={!customInputValue.trim() || manualPhase === 'thinking'}
                    aria-label="Send question"
                    style={{
                      flexShrink: 0,
                      width: '36px',
                      height: '36px',
                      borderRadius: '10px',
                      background: customInputValue.trim() ? '#1F4D3F' : 'rgba(31,77,63,0.15)',
                      border: 'none',
                      cursor: customInputValue.trim() ? 'pointer' : 'default',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background 0.15s ease',
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                      <path d="M2 7H12M8 3L12 7L8 11" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes vinaThink {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
        @keyframes cursorBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        @keyframes dropIn {
          from { opacity: 0; transform: translateY(-6px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          * { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; }
        }
      `}</style>

      {/* YouTube Video Modal */}
      {videoOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Watch the story"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.82)',
            padding: '16px',
          }}
          onClick={() => setVideoOpen(false)}
        >
          <div
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: '900px',
              aspectRatio: '16/9',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <iframe
              src="https://www.youtube.com/embed/jCQ5gGa197c?autoplay=1&rel=0"
              title="Watch the story"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
            />
          </div>
          <button
            aria-label="Close video"
            onClick={() => setVideoOpen(false)}
            style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: '#fff',
              fontSize: '20px',
              lineHeight: 1,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(4px)',
            }}
          >
            ✕
          </button>
        </div>
      )}
    </section>
  );
}